import ko from "knockout";
import _ from "lodash";
import moment from "moment";
import Bluebird from "bluebird";
import authService from "services/authService";
import notificationService from "services/notificationService";
import LOG from "services/loggingService";
import navHelper from "services/navHelper";
import toasts from "services/toastService";
import qualityManagementService from "services/qualityManagementService";
import userService from "services/userService";
import qualityAuthorization from "services/qualityAuth";
import apiCore from "services/apiCore";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import errorCallbackFactory from "util/errorCallbackFactory";
import screenMonitoringUtils from "util/screenMonitoringUtils";
import EmbeddedQualityFormEditor from "viewModels/EmbeddedQualityFormEditor";
import ConversationDetailsView from "viewModels/ConversationDetailsView";
import CalibrationResultsView from "viewModels/CalibrationResultsView";
import QualitySummaryView from "viewModels/QualitySummaryView";
import CreateEvaluationView from "viewModels/CreateEvaluationView";
import CoachingView from "viewModels/CoachingView";
import AuditView from "viewModels/AuditView";
import RecordingManagerView from "viewModels/RecordingManagerView";
import TabbedContentView from "viewModels/widgets/TabbedContentView";
import ViewModel from "viewModels/ViewModel";
import ConversationTimelineView from "viewModels/ConversationTimelineView";
import CustomerJourneyView from "viewModels/CustomerJourneyView";
import FormType from "models/composer/FormType";
import GIATranscriptView from "viewModels/GIATranscriptView";
import PlaybackHeaderView from "viewModels/PlaybackHeaderView";
import {
    getDecodedQueryParamsFromUri,
    getDecodedQueryParamsFromWindow,
    getHashContext,
    removeQueryParamFromWindow,
    getHashRoute,
    setQueryParams,
} from "util/uriUtil";
import { PlaybackContext } from "enums/PlaybackContext";
import "components/copy-button";
import {
    withHttpErrorRetry,
    MaxHttpRequestRetriesExceededError,
} from "util/withRequestRetry";
import {
    pickContextData,
    hasPermissionsToViewJourneyGadget,
} from "util/journeyUtils";
import { HttpStatusCode } from "enums/HttpStatusCode";
import SidePanelView from "./SidePanelView";
import EvaluationStatus from "enums/composer/EvaluationStatus";
import EvaluationSubmittedEventType from "enums/EvaluationSubmittedEventType";
import { SHORT_WEEKDAY_FULL_DATETIME_OPTIONS } from "util/DateTimeHelper";
import Icons from "enums/Icons";
import {
    createTabVisibilityHandler,
    getVisibilityDocument,
    isTabVisible,
} from "util/tabVisibilityUtils";

// Expected order of tabs relative to one another
const TAB_PLACEMENTS = {
    conversationDetails: 0,
    timeline: 1,
    qualitySummary: 2,
    transcript: 3,
    audit: 4,
    evaluation: 5,
    calibration: 6,
    journeys: 7,
    survey: 8,
};

const PLAYBACK_NAV_TAB_IDS = {
    conversationDetails: "conversation-details",
    timeline: "timeline",
    qualitySummary: "quality-summary",
    transcript: "transcript",
    audit: "audit-trails",
    evaluation: "evaluation",
    calibration: "calibrations",
    journeys: "customer-journeys",
    survey: "survey",
};

function getTabMenuItems(
    context,
    conversationId,
    endpoint,
    labelItem,
    tabId,
    tabPlacement = -1
) {
    return [
        {
            hash: navHelper.buildInteractionHash(
                context,
                conversationId,
                endpoint
            ),
            localHash: `#/${context}/${conversationId}/${endpoint}`,
            label: "playback.nav." + labelItem,
            cssClass: "nav-item-transient",
            tabId: tabId,
            tabPlacement: tabPlacement,
        },
    ];
}

/**
 * get all tab descriptors for the provided arguments
 *
 * @param  {string} conversationId
 * @param  {string} endpoint
 * @param  {string} labelItem
 * @returns {Object[]} array of tab menu item descriptors
 */
function getNavItemsForEndpoint(
    conversationId,
    endpoint,
    labelItem,
    tabId,
    tabPlacement
) {
    return [
        ...getTabMenuItems(
            PlaybackContext.ADMIN,
            conversationId,
            endpoint,
            labelItem,
            tabId,
            tabPlacement
        ),
        ...getTabMenuItems(
            PlaybackContext.AGENT,
            conversationId,
            endpoint,
            labelItem,
            tabId,
            tabPlacement
        ),
    ];
}

function activateLastNavItemForEndpoint(
    conversationId,
    endpoint,
    labelItem,
    tabId,
    tabPlacement,
    navMenu
) {
    const detailsTabs = getNavItemsForEndpoint(
        conversationId,
        endpoint,
        labelItem,
        tabId,
        tabPlacement
    );
    detailsTabs.forEach((tab) => {
        if (navMenu.containsNavItem(tab)) {
            navMenu.setActive(tab.localHash);
        }
    });
}

const PlaybackView = ViewModel.extend(function (conversationId, options) {
    const self = this;

    self.Icons = Icons;

    let hashContext;
    try {
        hashContext = getHashContext(
            window.location.hash,
            featureToggleService.isEnabled(
                FeatureToggle.REC_5171_USE_NEW_ANALYTICS_ROUTE
            )
        );
    } catch (err) {
        LOG.error(err);
    }

    const timeFormatWithSeconds = ko.i18n.renderString(
        "interactionsList.timeFormatSeconds"
    );
    self.conversationId = ko.observable(conversationId);
    self.sidepanel = self.subView(SidePanelView);
    self.tabbedDetails = self.subView(TabbedContentView);
    self.conversation = ko.observable();
    self.conversationDetailsV6 = ko.observable();
    self.isFlareThemeEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_1418_IDUI_SPARK_V4_FLARE
    );
    self.journeyContexts = self.computed(() => {
        const conversationDetails = self.conversationDetailsV6();

        if (!conversationDetails) return [];
        return pickContextData(conversationDetails);
    });

    self.evaluationId = ko.observable();
    self.surveyId = ko.observable();
    self.calibrations = ko.observableArray();
    self.isConversationLoading = ko.observable(true);
    self.isCalibrationsLoading = ko.observable(true);
    self.isEvaluationSummaryLoading = ko.observable(true);
    self.isTranscriptLoading = ko.observable(true);
    self.hasParticipantToEvaluate = ko.observable(true);
    self.hasParkedInteraction = ko.observable(false);
    self.tabRedirect = ko.observable(false);
    self.isAgent = authService.hasAnyAuthorization([
        "perm:routing:agent:onqueue",
    ]);
    self.qualityEvaluator = authService.hasAnyAuthorization([
        "qualityEvaluator",
    ]);
    self.qualityAdmin = authService.hasAnyAuthorization(["qualityAdmin"]);
    self.isCallTerminatedMessage = ko.observable();
    self.canEditEvaluationOrScore =
        qualityAuthorization.canEditEvaluation() ||
        qualityAuthorization.canEditScore();
    const evaluationNotificationTopic = `v2.conversations.${self.conversationId()}.evaluations`;

    self.canViewConversationCustomerJourney =
        hasPermissionsToViewJourneyGadget();
    self.playbackContext = ko.observable();
    self.needsWrapup = ko.observable(false);
    self.acdVoicemailIds = ko.observable();
    self.isCurrentUserOnConversation = ko.observable(false);
    self.activeRecordingView = ko.observable();
    self.agentToMonitorCoachObservable = ko.observable();
    self.hideInternalParticipantPopover = ko.observable(true);
    self.hideExternalParticipantPopover = ko.observable(true);

    self.conversationOriginatingDirection = self.computed(() => {
        const conversationDetails = self.conversationDetailsV6();
        if (conversationDetails) {
            return conversationDetails.originatingDirection;
        } else {
            return undefined;
        }
    });

    const isMessageParkingEnabled = featureToggleService.isEnabled(
        FeatureToggle.MESSAGE_PARKING
    );

    const isAutomatedEvaluationCompletionToggleEnabled =
        featureToggleService.isEnabled(
            FeatureToggle.AUTOMATED_EVALUATION_COMPLETION
        ) &&
        featureToggleService.isEnabled(
            FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
        );

    const isScreenMonitoringEnabled = featureToggleService.isEnabled(
        FeatureToggle.WEM_437_SCREEN_MONITORING
    );

    /**
     *  ACTIVE CALL
     * @type {*}
     */
    self.isActiveInteraction = self.computed(function () {
        // Call is not active if it has an endTime.
        return (
            self.conversation() &&
            self.conversation().id &&
            self.conversation().hasOwnProperty("startTime") &&
            !self.conversation().hasOwnProperty("endTime")
        );
    });
    const recordingManagerOptions = _.extend({}, options, {
        isNotActive: ko.computed(
            () => !self.isConversationLoading() && !self.isActiveInteraction()
        ),
    });

    const acdVoicemailInfo = {
        acdVoicemailIds: self.acdVoicemailIds,
        isCurrentUserOnConversation: self.isCurrentUserOnConversation,
    };

    self.recordingManagerView = self.subView(
        RecordingManagerView,
        self.conversationId,
        acdVoicemailInfo,
        recordingManagerOptions,
        self.activeRecordingView,
        self.conversationOriginatingDirection
    );

    self.coachingView = self.subView(
        CoachingView,
        self.conversationId,
        self.isActiveInteraction
    );

    self.isLoadingMultiple = self.computed(function () {
        return (
            self.recordingManagerView.isRecordingLoading() &&
            !self.recordingManagerView.showLongRunningMessage()
        );
    });

    // Add the computed functions that combine feature toggle and loading state
    self.shouldWaitForRecordingLoad = self.computed(function () {
        const isLoadTranscriptWithoutRecordingEnabled =
            featureToggleService.isEnabled(
                FeatureToggle.WEM_1249_LOAD_TRANSCRIPT_WITHOUT_RECORDING
            );
        return (
            !isLoadTranscriptWithoutRecordingEnabled && self.isLoadingMultiple()
        );
    });

    self.displayWrapupView = self.computed(function () {
        return (
            self.needsWrapup() &&
            !self.recordingManagerView.hasRecordings() &&
            !self.isActiveInteraction()
        );
    });

    self.hasConnectedCallback = self.computed(function () {
        if (self.conversation() && self.conversation().participants) {
            const participants = self.conversation().participants();
            return _.some(participants, function (participant) {
                return (
                    participant.callbacks &&
                    participant.callbacks.length > 0 &&
                    _.some(participant.callbacks, function (callback) {
                        return _.some(callback.segments, function (segment) {
                            return !segment.endTime;
                        });
                    })
                );
            });
        }
        return false;
    });

    self.parkedEndTime = self.computed(function () {
        let parkedTime = ko.i18n.renderString("playback.recording.live");
        if (self.conversation() && self.conversation().participants) {
            const participants = self.conversation().participants();
            participants.forEach(function (participant) {
                if (
                    participant.purpose &&
                    (participant.purpose.toLowerCase() === "agent" ||
                        participant.purpose.toLowerCase() === "user") &&
                    (participant.emails ||
                        (participant.messages && isMessageParkingEnabled))
                ) {
                    if (participant.emails) {
                        participant.emails.forEach(function (email) {
                            if (email.state === "parked") {
                                parkedTime = ko.i18n.formatDate(
                                    ko.unwrap(email.parkTime),
                                    timeFormatWithSeconds,
                                    null,
                                    SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
                                );
                            }
                        });
                    }
                    if (participant.messages) {
                        participant.messages.forEach(function (message) {
                            if (message.state === "parked") {
                                parkedTime = ko.i18n.formatDate(
                                    ko.unwrap(message.parkTime),
                                    timeFormatWithSeconds,
                                    null,
                                    SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
                                );
                            }
                        });
                    }
                }
            });
        }
        return parkedTime;
    });

    //Check if participant is connected
    const isParticipantConnected = function (participant) {
        let participants = [];
        if (self.conversationDetailsView.hasCalls()) {
            participants = participant.calls;
        }
        if (self.conversationDetailsView.hasMessages()) {
            participants = participant.messages;
        }
        if (
            isScreenMonitoringEnabled &&
            self.conversationDetailsView.hasEmails()
        ) {
            participants = participant.emails;
        }
        if (
            isScreenMonitoringEnabled &&
            participant.screenMonitorings &&
            participant.screenMonitorings.length > 0
        ) {
            participants = participant.screenMonitorings;
        }

        return _.some(participants, (item) =>
            _.some(
                item.segments,
                (segment) =>
                    segment.type === "Interact" ||
                    segment.type === "Coaching" ||
                    segment.type === "Monitoring" ||
                    segment.type === "Screenmonitoring"
            )
        );
    };
    // check if map have particular agent map , if yes add or update coaching / monitoring session else create new map with new session
    const addMonitorsAndCoachToMap = function (
        agentToSupervisors,
        supervisor,
        agentUserId,
        supervisorUserId
    ) {
        if (!agentToSupervisors.has(agentUserId)) {
            agentToSupervisors.set(agentUserId, new Map());
        }

        const supervisors = agentToSupervisors.get(agentUserId);

        // If WEM-437 feature toggle is enabled, merge properties when same user has multiple participants
        // (e.g., one for monitor/coach and one for screenMonitor)
        // Otherwise, use existing behavior (overwrite)
        if (isScreenMonitoringEnabled && supervisors.has(supervisorUserId)) {
            const existingSupervisor = supervisors.get(supervisorUserId);

            // Merge only if both are active or both are inactive, otherwise keep only the active one
            const bothActive =
                existingSupervisor.isActive && supervisor.isActive;
            const bothInactive =
                !existingSupervisor.isActive && !supervisor.isActive;

            if (bothActive || bothInactive) {
                // Merge properties: preserve existing properties and add new ones
                supervisors.set(supervisorUserId, {
                    ...existingSupervisor,
                    ...supervisor,
                    // Merge boolean flags using OR logic to preserve both states
                    isMonitor:
                        existingSupervisor.isMonitor ||
                        supervisor.isMonitor ||
                        false,
                    isCoach:
                        existingSupervisor.isCoach ||
                        supervisor.isCoach ||
                        false,
                    isScreenMonitor:
                        existingSupervisor.isScreenMonitor ||
                        supervisor.isScreenMonitor ||
                        false,
                    // Use the most recent participantView if available
                    participantView:
                        supervisor.participantView ||
                        existingSupervisor.participantView,
                    // isActive should match the state (both are same)
                    isActive: existingSupervisor.isActive,
                });
            } else {
                // One is active, one is inactive - keep only the active one
                if (supervisor.isActive) {
                    supervisors.set(supervisorUserId, supervisor);
                }
                // If existingSupervisor is active and supervisor is inactive, keep existingSupervisor (do nothing)
            }
        } else {
            // Existing behavior: overwrite if entry exists, or set if it doesn't
            supervisors.set(supervisorUserId, supervisor);
        }

        return agentToSupervisors;
    };

    self.agentToMonitorCoachMap = self.computed(function () {
        const conversation = self.conversation();
        let agentToSupervisors = new Map();
        const participantIdToUserIdMap = new Map();

        if (conversation?.participants) {
            _.each(
                ko.unwrap(self.conversation().participants),
                function (participant) {
                    // keep track of all participant ID's to user ID's
                    participantIdToUserIdMap.set(
                        participant.id,
                        participant.userId
                    );

                    // External user will not have userID , add tag "external" to identify if external user is monitored / coached
                    if (
                        participant.participantType?.toLowerCase() ===
                        "external"
                    ) {
                        participantIdToUserIdMap.set(
                            participant.id,
                            "external"
                        );
                    }
                    // Get all unique targetUserIds from ScreenMonitoring object
                    let participantTargetUserIds = [];
                    if (isScreenMonitoringEnabled) {
                        const participantScreenMonitorings = ko.unwrap(
                            participant.screenMonitorings
                        );
                        participantTargetUserIds =
                            screenMonitoringUtils.getAllUniqueTargetUserIds(
                                participantScreenMonitorings
                            );
                    }
                    const hasTargetUserIds =
                        participantTargetUserIds.length > 0;
                    if (
                        (participant.monitoredParticipantId ||
                            participant.coachedParticipantId ||
                            hasTargetUserIds) &&
                        isParticipantConnected(participant)
                    ) {
                        const supervisor = {};
                        const supervisorView = _.find(
                            self.conversationDetailsView.internalParticipantViews(),
                            function (view) {
                                return view.participantId === participant.id;
                            }
                        );
                        supervisor.participantView = supervisorView;
                        supervisor.isActive = _.isUndefined(
                            participant.endTime
                        );
                        //  check if participant is monitor and not monitoring external users
                        if (
                            participant.monitoredParticipantId &&
                            participantIdToUserIdMap.get(
                                participant.monitoredParticipantId
                            ) !== "external"
                        ) {
                            supervisor.isMonitor = true;
                            agentToSupervisors = addMonitorsAndCoachToMap(
                                agentToSupervisors,
                                supervisor,
                                participantIdToUserIdMap.get(
                                    participant.monitoredParticipantId
                                ),
                                participant.userId
                            );
                        } else if (
                            // check if participant is coach and not coaching external users
                            participant.coachedParticipantId &&
                            participantIdToUserIdMap.get(
                                participant.coachedParticipantId
                            ) !== "external"
                        ) {
                            supervisor.isCoach = true;
                            agentToSupervisors = addMonitorsAndCoachToMap(
                                agentToSupervisors,
                                supervisor,
                                participantIdToUserIdMap.get(
                                    participant.coachedParticipantId
                                ),
                                participant.userId
                            );
                        }
                        // Handle multiple targetUserIds - add supervisor for each unique targetUser
                        if (isScreenMonitoringEnabled && hasTargetUserIds) {
                            participantTargetUserIds.forEach(function (
                                targetUserId
                            ) {
                                // Create a new supervisor object to avoid reference sharing
                                const screenMonitorSupervisor = {
                                    participantView: supervisor.participantView,
                                    isActive: supervisor.isActive,
                                    isScreenMonitor: true,
                                    // Preserve other supervisor properties if they exist
                                    isMonitor: supervisor.isMonitor || false,
                                    isCoach: supervisor.isCoach || false,
                                };
                                agentToSupervisors = addMonitorsAndCoachToMap(
                                    agentToSupervisors,
                                    screenMonitorSupervisor,
                                    targetUserId,
                                    participant.userId
                                );
                            });
                        }
                    }
                }
            );
        }
        if (
            !_.isEqual(self.agentToMonitorCoachObservable(), agentToSupervisors)
        ) {
            self.agentToMonitorCoachObservable(agentToSupervisors);
        }
    });

    self.coachedParticipants = self.computed(function () {
        const coachedUsers = [];
        if (self.agentToMonitorCoachObservable()) {
            self.agentToMonitorCoachObservable().forEach(
                (supervisorMap, agentId) => {
                    supervisorMap.forEach((obj) => {
                        if (obj.isActive && obj.isCoach) {
                            coachedUsers.push(agentId);
                        }
                    });
                }
            );
        }
        return coachedUsers;
    });
    self.monitoredParticipants = self.computed(function () {
        const monitoredUsers = [];
        if (self.agentToMonitorCoachObservable()) {
            self.agentToMonitorCoachObservable().forEach(
                (supervisorMap, agentId) => {
                    supervisorMap.forEach((obj) => {
                        if (obj.isActive && obj.isMonitor) {
                            monitoredUsers.push(agentId);
                        }
                    });
                }
            );
        }
        return monitoredUsers;
    });
    const recordingManagerMediaTypes = {
        hasMessages: self.recordingManagerView.hasMessages,
        hasEmails: self.recordingManagerView.hasEmail,
        hasChats: self.recordingManagerView.hasChats,
        hasCalls: self.recordingManagerView.hasCalls,
        isAcdvoicemail: self.recordingManagerView.isAcdvoicemail,
    };
    const conversationDetailsOptions = {
        recordingInformation: self.recordingManagerView.recordingInformation,
        conversationId: conversationId,
        internalUsers: self.recordingManagerView.internalUsers,
        externalContacts: self.recordingManagerView.externalContacts,
        recordingManagerMediaTypes,
    };

    self.conversationDetailsView = self.subView(
        ConversationDetailsView,
        self.conversation,
        self.conversationDetailsV6,
        conversationDetailsOptions,
        self.recordingManagerView.shouldDisplayEmailRecordingInfo,
        self.coachedParticipants,
        self.monitoredParticipants
    );

    self.conversationTimelineView = self.subView(
        ConversationTimelineView,
        self.conversationDetailsV6,
        self.conversation
    );

    const getUniqueParticipantViews = (participantViews, sameParticipants) => {
        const uniqueViews = [];
        participantViews().forEach((view) => {
            const duplicateView = uniqueViews.find((addedView) =>
                sameParticipants(view, addedView)
            );
            if (!duplicateView) {
                // find participants for the same user which can be monitored
                const monitorableViews = participantViews().filter(
                    (otherView) => {
                        return (
                            sameParticipants(view, otherView) &&
                            (otherView.isMonitored() ||
                                otherView.canMonitor() ||
                                otherView.isMonitoredMessage() ||
                                otherView.canMonitorMessage())
                        );
                    }
                );

                // use the monitorable participants if they exist or the last participant for the user
                if (monitorableViews.length > 0) {
                    uniqueViews.push(...monitorableViews);
                } else {
                    uniqueViews.push(
                        _.findLast(participantViews(), (otherView) =>
                            sameParticipants(view, otherView)
                        )
                    );
                }
            }
        });

        return uniqueViews;
    };

    self.uniqueInternalParticipantViews = self.computed(() => {
        const sameInternalParticipants = (view1, view2) => {
            return view1.userId === view2.userId;
        };
        const uniqueViews = getUniqueParticipantViews(
            self.conversationDetailsView.internalParticipantViews,
            sameInternalParticipants
        );
        // filters out users who have monitored or coached the conversation
        const agentsView = uniqueViews.filter((view) => {
            return (
                !view.isMonitor() &&
                !view.isMonitorMessage() &&
                !view.isCoach() &&
                !view.isScreenMonitor()
            );
        });
        return agentsView;
    });

    self.uniqueExternalParticipantViews = self.computed(() => {
        const sameExternalParticipants = (view1, view2) => {
            // if externalContactId is undefined then compare the displayed values
            return (
                view1.externalContactId === view2.externalContactId &&
                view1.userDisplayString() === view2.userDisplayString() &&
                view1.nameDisplayString() === view2.nameDisplayString() &&
                view1.addressDisplayString() === view2.addressDisplayString() &&
                view1.extraAddressDisplayInformation() ===
                    view2.extraAddressDisplayInformation()
            );
        };
        return getUniqueParticipantViews(
            self.conversationDetailsView.externalParticipantViews,
            sameExternalParticipants
        );
    });

    self.isSingleInternalParticipantView = self.computed(() => {
        if (
            self.uniqueInternalParticipantViews().length === 1 &&
            _.isEmpty(self.agentToMonitorCoachObservable())
        ) {
            return true;
        }
        return false;
    });

    self.isSingleExternalParticipantView = self.computed(() => {
        if (self.uniqueExternalParticipantViews().length === 1) {
            return true;
        }
        return false;
    });

    self.toggleInternalParticipantPopover = function () {
        self.hideInternalParticipantPopover(
            !self.hideInternalParticipantPopover()
        );
        self.dismissExternalParticipantPopover();
    };

    self.toggleExternalParticipantPopover = function () {
        self.hideExternalParticipantPopover(
            !self.hideExternalParticipantPopover()
        );
        self.dismissInternalParticipantPopover();
    };

    self.dismissInternalParticipantPopover = function () {
        self.hideInternalParticipantPopover(true);
    };

    self.dismissExternalParticipantPopover = function () {
        self.hideExternalParticipantPopover(true);
    };

    if (options && options.playbackContext) {
        self.playbackContext(options.playbackContext);
    }

    let hasAvailableTranscript;

    /**
     * If the user manually redirects while transcript is loading, stop the event listener
     * from defaulting to the details or transcript tab after a response is received.
     * It should still choose a default in case a redirect is needed without reloading the app.
     */
    const stopTabDefaultBehavior = function () {
        if (typeof hasAvailableTranscript === "undefined") {
            self.tabRedirect(false);
            window.removeEventListener("message", self.chooseDefaultTab);
            window.addEventListener("message", self.chooseDefaultTab);
        }
    };

    const checkInternalParticipant = function (participants) {
        return !!_.find(participants(), function (participant) {
            if (participant.purpose) {
                return (
                    participant.purpose.toLowerCase() === "user" ||
                    participant.purpose.toLowerCase() === "agent"
                );
            }

            if (participant.participantType) {
                return participant.participantType.toLowerCase() === "internal";
            }
            return false;
        });
    };

    self.hasAgentTransferred = self.computed(function () {
        // This is to fix flickering of transfer label during live call. self.conversationDetailsV6() gets reset to null every ~5 seconds
        // during live interactions, this will toggle between true & false, We should not reset value to flase if its alreday set to true
        if (self.hasAgentTransferred && self.hasAgentTransferred.peek()) {
            return true;
        }
        const conversation = self.conversationDetailsV6();
        let agentTransferred = false;
        if (conversation && conversation.participants) {
            _.each(conversation.participants, function (participant) {
                if (
                    participant.purpose &&
                    participant.purpose.toLowerCase() === "agent"
                ) {
                    _.each(participant.sessions, function (communication) {
                        if (communication.segments) {
                            _.each(communication.segments, function (segment) {
                                if (
                                    segment.disconnectType &&
                                    (segment.disconnectType.toLowerCase() ==
                                        "transfer" ||
                                        segment.disconnectType.toLowerCase() ==
                                            "consulttransfer" ||
                                        segment.disconnectType.toLowerCase() ==
                                            "forwardtransfer" ||
                                        segment.disconnectType.toLowerCase() ==
                                            "conferencetransfer")
                                ) {
                                    agentTransferred = true;
                                }
                            });
                        }
                    });
                }
            });
        }
        return agentTransferred;
    });
    self.checkInteractionHasLiveParkedSegments = self.computed(function () {
        const conversation = self.conversationDetailsV6();
        let parkedInteraction = false;
        if (conversation && conversation.participants) {
            conversation.participants.forEach(function (participant) {
                if (
                    participant.purpose &&
                    participant.purpose.toLowerCase() === "agent"
                ) {
                    participant.sessions.forEach(function (communication) {
                        if (communication.segments) {
                            communication.segments.some(function (segment) {
                                if (
                                    segment.segmentType &&
                                    segment.segmentType.toLowerCase() ==
                                        "parked" &&
                                    !segment.segmentEnd
                                ) {
                                    parkedInteraction = true;
                                    return true;
                                }
                            });
                        }
                    });
                }
            });
        }
        // This is to fix flickering of labels between `live` and `parked` during parked call. self.conversationDetailsV6() gets reset to null every ~5 seconds
        // during parked interactions, this will toggle between true & false, We should not update the value if its already set to same value

        if (conversation && parkedInteraction !== self.hasParkedInteraction()) {
            self.hasParkedInteraction(parkedInteraction);
            self.liveHeader.isParked(parkedInteraction);
        }
    });

    const checkIfNeedsWrapup = function (participants) {
        return !!_.find(participants(), function (participant) {
            return participant.wrapupRequired && !participant.wrapup;
        });
    };

    const getAcdVoicemailIds = function (conversation) {
        const callbacks = _.compact(
            _.flatMap(conversation.participants(), "callbacks")
        );
        const sortedCallbacks = _.sortBy(callbacks, ["connectedTime"]);
        const voicemailIds = _.compact(
            _.map(sortedCallbacks, function (callback) {
                return callback.voicemail ? callback.voicemail.id : null;
            })
        );
        return _.uniq(voicemailIds);
    };

    /**
     * Adds the timeline tab to the conversation page, then loads the template, along with the appropriate view
     * Adds the conversation timeline tab to the conversation page, then loads the template, along with the appropriate view
     * It also adds the css along with the label to the Timeline Tab
     * @returns void
     */
    const addTimelineTab = function (conversationId) {
        if (!self.tabbedDetails.containsTab("timeline")) {
            const tabContentViews = {};
            tabContentViews.timeline = {
                view: function () {
                    return self.conversationTimelineView;
                },
                template: "templates/timelineView",
                persist: true,
                tabId: PLAYBACK_NAV_TAB_IDS.timeline,
            };
            const context =
                hashContext ||
                (self.canEditEvaluationOrScore
                    ? PlaybackContext.ADMIN
                    : PlaybackContext.AGENT);
            const tabMenuItems = getTabMenuItems(
                context,
                conversationId,
                "timeline",
                "timeline",
                PLAYBACK_NAV_TAB_IDS.timeline,
                TAB_PLACEMENTS.timeline
            );
            self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
        }
    };

    const getConversation = function () {
        self.isConversationLoading(true);
        qualityManagementService
            .getConversation(self.conversationId())
            .then(self.conversation)
            .then(function () {
                addTimelineTab(self.conversationId());
                self.recordingManagerView.setConversation(self.conversation);
                if (self.conversation() && self.conversation().participants) {
                    self.coachingView.participants(
                        ko.unwrap(self.conversation().participants)
                    );
                }
            })
            .catch(
                errorCallbackFactory.createCallback({
                    namespace: "playback.conversationRequest",
                })
            )
            .finally(function () {
                if (!self.conversation()) {
                    self.recordingManagerView.callRecordingItemContainer(
                        false,
                        self.conversationId()
                    );
                }
                if (!self.acdVoicemailIds()) {
                    self.acdVoicemailIds([]);
                }
                self.isConversationLoading(false);
                initializeHeader();
            });
    };
    getConversation();

    /**
     * @param  {string} conversationId
     */
    function getAnalyticsV6ConversationDetails(conversationId) {
        self.conversationDetailsV6(null);
        return apiCore.v2
            .get(`/analytics/conversations/${conversationId}/details`)
            .then(self.conversationDetailsV6);
    }

    const getAnalyticsV6ConversationDetailsWithRetry = withHttpErrorRetry(
        getAnalyticsV6ConversationDetails,
        [HttpStatusCode.NOT_FOUND],
        { maxRetries: 5 }
    );

    // initial request to get and populate analytics v6 conversation
    // details record when the view is constructed
    getAnalyticsV6ConversationDetailsWithRetry(self.conversationId()).catch(
        MaxHttpRequestRetriesExceededError,
        LOG.warn
    );

    const getEvaluations = function () {
        const userType =
            hashContext === PlaybackContext.ADMIN
                ? "evaluator"
                : PlaybackContext.AGENT;

        const evaluationOptions = {
            agentId: userService.currentUser().id,
            conversationId: self.conversationId(),
            userType: userType,
        };

        if (userType === "agent") {
            evaluationOptions.isReleased = true;
            evaluationOptions.evaluationState = [EvaluationStatus.FINISHED];
        }

        qualityManagementService
            .getAssignedEvaluations(evaluationOptions)
            .then(function (response) {
                self.evaluations(response);
            })
            .catch(function (error) {
                const httpStatus =
                    error.type === "http" ? error.details.status : undefined;
                if (!(httpStatus && httpStatus === 401)) {
                    errorCallbackFactory.createCallback({
                        namespace: "playback.evaluationsRequest",
                    })(error);
                }
            })
            .finally(function () {
                self.isEvaluationsLoading(false);
            });
    };

    const getCalibrations = function () {
        if (userService.currentUser()) {
            if (qualityAuthorization.canViewCalibration()) {
                qualityManagementService
                    .getCalibrations({
                        conversationId: self.conversationId(),
                        calibratorId: userService.currentUser().id,
                    })
                    .then(self.calibrations)
                    .catch(
                        errorCallbackFactory.createCallback({
                            namespace: "playback.calibrationsRequest",
                        })
                    )
                    .finally(function () {
                        self.isCalibrationsLoading(false);
                    });
            }
        }
    };
    getCalibrations();
    self.subscribe(userService.currentUser, getCalibrations);

    // header for a historical call
    self.historicalInteractionType = ko.computed(function () {
        if (
            featureToggleService.isEnabled(FeatureToggle.WEM_1128_IDUI_SPARK_V4)
        ) {
            if (self.conversationDetailsView.hasCalls()) {
                if (self.conversationDetailsView.isAcdVoicemail()) {
                    return { icon: "voicemail", title: "voicemail" };
                } else {
                    return { icon: "phone", title: "audio" };
                }
            }
            if (self.conversationDetailsView.hasChats()) {
                return { icon: "fa/comment-regular", title: "chat" };
            }
            if (self.conversationDetailsView.hasEmails()) {
                return { icon: "message-email", title: "email" };
            }
            if (self.conversationDetailsView.hasMessages()) {
                return { icon: "fa/message-dots-regular", title: "message" };
            } else {
                return null;
            }
        } else {
            if (self.conversationDetailsView.hasCalls()) {
                if (self.conversationDetailsView.isAcdVoicemail())
                    return { icon: "voicemail", title: "voicemail" };
                else return { icon: "new-call", title: "call" };
            }
            if (self.conversationDetailsView.hasChats())
                return { icon: "new-chat", title: "chat" };
            if (self.conversationDetailsView.hasEmails())
                return { icon: "new-email", title: "email" };
            if (self.conversationDetailsView.hasMessages())
                return { icon: "new-message", title: "message" };
            else return null;
        }
    });

    // header for a live call
    self.liveInteractionType = ko.computed(function () {
        if (self.conversationDetailsView.hasCalls()) return "call";
        if (self.conversationDetailsView.hasChats()) return "chat";
        if (self.conversationDetailsView.hasEmails()) return "email";
        if (self.conversationDetailsView.hasMessages()) return "message";
        return null;
    });

    self.liveHeader = self.subView(
        PlaybackHeaderView,
        ko.i18n.renderString("playback.recording.duration"),
        self.liveInteractionType,
        self.isActiveInteraction,
        self.hasParkedInteraction()
    );

    const initializeHeader = function () {
        let formattedStartTime;

        if (
            self.hasParkedInteraction() &&
            featureToggleService.isEnabled(FeatureToggle.EMAIL_PARKING)
        ) {
            self.liveHeader.endTime(self.parkedEndTime());
        } else {
            self.liveHeader.endTime(
                ko.i18n.renderString("playback.recording.live")
            );
        }
        if (self.conversation() && self.conversation().startTime) {
            formattedStartTime = ko.i18n.formatDate(
                self.conversation().startTime(),
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
            self.liveHeader.startTime(formattedStartTime);
            self.liveHeader.isParked(self.hasParkedInteraction());
        }
    };

    // keep updating the duration when the call isActive
    function updateDuration() {
        if (self.conversation() && self.conversation().startTime) {
            const elapsedTime = moment().diff(self.conversation().startTime());
            self.liveHeader.setDuration(elapsedTime);
        }
    }

    const ACTIVECONVERSATIONPOLL_SECONDS = 5;
    const TAB_VISIBILITY_REFETCH_DELAY_MS = 1000;
    let activeConversationUpdateInterval;
    let conversationUpdateCounter = 0;

    // Track disposal state for visibility handler
    let isViewDisposed = false;

    // Track if conversation data fetch is in progress to prevent overlapping requests
    let isConversationFetchInProgress = false;

    /**
     * Fetches conversation data with proper tracking to prevent overlapping requests.
     * Only used when the reduce inactive tab polling feature toggle is enabled.
     */
    const fetchConversationData = function () {
        if (isConversationFetchInProgress) {
            return;
        }

        isConversationFetchInProgress = true;

        Promise.allSettled([
            getConversation(),
            getAnalyticsV6ConversationDetails(self.conversationId()),
        ]).finally(() => {
            isConversationFetchInProgress = false;
            // Reset counter after fetch completes to ensure proper 5-second interval
            conversationUpdateCounter = 0;
        });
    };

    // Reduce inactive tab polling - behind feature toggle
    const isReduceInactiveTabPollingEnabled = featureToggleService.isEnabled(
        FeatureToggle.REC_6867_OPTIMIZE_API_CALLS_IN_IDUI
    );

    // Get the visibility document once and reuse it for both handler and polling check
    const visibilityDoc = isReduceInactiveTabPollingEnabled
        ? getVisibilityDocument()
        : null;

    // Setup tab visibility handler to refetch data when tab becomes visible
    let cleanupVisibilityHandler = null;
    if (isReduceInactiveTabPollingEnabled) {
        cleanupVisibilityHandler = createTabVisibilityHandler({
            onVisible: () => {
                // Fetch latest data when tab becomes visible
                fetchConversationData();
            },
            onHidden: () => {
                // Reset counter when tab becomes hidden to prevent duplicate fetches
                conversationUpdateCounter = 0;
            },
            debounceMs: TAB_VISIBILITY_REFETCH_DELAY_MS,
            shouldExecute: () => {
                // Check if view is not disposed and is active
                return (
                    !isViewDisposed &&
                    self.conversationDetailsView &&
                    self.conversationDetailsView.active &&
                    self.conversationDetailsView.active.peek()
                );
            },
            visibilityDocument: visibilityDoc,
        });
    }

    self.computed(function () {
        clearInterval(activeConversationUpdateInterval);
        if (self.conversationDetailsView.active()) {
            conversationUpdateCounter = 0;
            activeConversationUpdateInterval = setInterval(function () {
                if (
                    isReduceInactiveTabPollingEnabled &&
                    !isTabVisible(visibilityDoc)
                ) {
                    return;
                }

                const detailsView = self.conversationDetailsView;
                const timelineView = self.conversationTimelineView;

                if (
                    detailsView.active.peek() ||
                    timelineView.active.peek() ||
                    self.hasConnectedCallback()
                ) {
                    if (timelineView.updateLiveSegments) {
                        timelineView.updateLiveSegments();
                    }

                    if (detailsView.updateDuration) {
                        detailsView.updateDuration();
                    }

                    if (
                        ++conversationUpdateCounter >=
                        ACTIVECONVERSATIONPOLL_SECONDS
                    ) {
                        if (isReduceInactiveTabPollingEnabled) {
                            // proper tracking to prevent overlapping requests behind FT
                            fetchConversationData();
                        } else {
                            conversationUpdateCounter = 0;
                            getConversation();
                            getAnalyticsV6ConversationDetails(
                                self.conversationId()
                            );
                        }
                    }
                }

                // utlize the active status in conversationDetailView to update the duration
                updateDuration();
            }, 1000);
        }
    });

    /**
     * TAB CONTROL
     * @param conversationId
     */
    const addConversationTab = function (conversationId) {
        if (!self.tabbedDetails.containsTab("conversationDetailsView")) {
            const tabContentViews = {
                conversationDetailsView: {
                    view: function () {
                        return self.conversationDetailsView;
                    },
                    template: "templates/conversationDetails",
                    persist: true,
                    tabId: PLAYBACK_NAV_TAB_IDS.conversationDetails,
                },
            };

            self.tabbedDetails.addTabViews(
                [
                    {
                        hash: navHelper.buildInteractionHash(
                            hashContext,
                            conversationId,
                            "details"
                        ),
                        localHash: `#/${hashContext}/${conversationId}/details`,
                        label: "playback.nav.conversationDetails",
                        matchExactly: true,
                        tabPlacement: TAB_PLACEMENTS.conversationDetails,
                        tabId: PLAYBACK_NAV_TAB_IDS.conversationDetails,
                    },
                ],
                tabContentViews
            );
        }
    };

    const activateTabIfNeeded = function (/*tabKey, additionalArgs*/) {
        const tabName = arguments[0];

        // Workaround for (intentional) behavior change in SwitchView where a subview
        // flagged as persistent will get disposed anyway if it is activated
        // when it is already active.
        if (self.tabbedDetails.currentViewData()) {
            const activeTabName = self.tabbedDetails.currentViewData().viewKey;
            if (activeTabName === tabName) {
                return self.tabbedDetails.currentViewData();
            }
        }

        return self.tabbedDetails.setActiveTabView.apply(
            self.tabbedDetails,
            arguments
        );
    };

    self.activateConversationDetailsTab = function () {
        stopTabDefaultBehavior();
        addConversationTab(self.conversationId());
        activateTabIfNeeded("conversationDetailsView");
        activateLastNavItemForEndpoint(
            self.conversationId(),
            "details",
            "details",
            PLAYBACK_NAV_TAB_IDS.conversationDetails,
            TAB_PLACEMENTS.conversationDetails,
            self.tabbedDetails.navMenu
        );
    };

    const navigateToQualitySummary = function () {
        navHelper.navigateTo(
            navHelper.buildInteractionHash(
                "admin",
                self.conversationId(),
                "qualitySummary"
            )
        );
    };

    const createCalibrationCallback = function (newCalibration) {
        const calibration = self
            .calibrations()
            .entities.find((entity) => entity.id === newCalibration.id);
        if (!calibration?.id) {
            self.calibrations().entities.push(newCalibration);
        }
        const destination = navHelper.buildInteractionHash(
            "admin",
            self.conversationId(),
            `calibrations/${newCalibration.id}`
        );
        navHelper.navigateTo(destination);
    };

    const newEvaluationCallback = function (newEvaluation) {
        let destination;
        if (newEvaluation) {
            if (newEvaluation.evaluator.id === authService.principalId()) {
                const isAssigneeAndDisputesFTEnabled =
                    featureToggleService.isEnabled(
                        FeatureToggle.EVALUATION_ASSIGNEE
                    ) &&
                    featureToggleService.isEnabled(
                        FeatureToggle.EVALUATION_DISPUTES
                    );
                destination = navHelper.buildInteractionHash(
                    "admin",
                    self.conversationId(),
                    `qualitySummary/?evaluationId=${newEvaluation.id}`
                );
                const path = setQueryParams(destination, {
                    ...getDecodedQueryParamsFromUri(destination),
                    ["assignmentPanel"]: false,
                });
                navHelper.navigateTo(
                    isAssigneeAndDisputesFTEnabled ? path : destination,
                    undefined,
                    false
                );
            } else {
                const notificationTitle = "playback.nav.evaluation.created";
                const notificationMsg = "playback.nav.evaluation.created.for";
                const selectedEvaluatorName = ko.unwrap(
                    newEvaluation.evaluator.name
                );
                toasts.notifyMessage(notificationTitle, {
                    bodyMessageKey: notificationMsg,
                    messageData: { evaluatorName: selectedEvaluatorName },
                    icon: "pc-icon-messages",
                });
                destination = navHelper.buildInteractionHash(
                    "admin",
                    self.conversationId(),
                    "qualitySummary"
                );
                navHelper.navigateTo(destination, undefined, false);
            }
        }
    };

    function removeEvaluationTab(
        playbackContext,
        assignedEvaluationId,
        activateTabKey
    ) {
        const tabMenuItems = getTabMenuItems(
            playbackContext,
            self.conversationId(),
            "evaluations/" + assignedEvaluationId,
            "evaluation",
            PLAYBACK_NAV_TAB_IDS.qualitySummary
        );

        if (
            self.tabbedDetails.containsTabAndHash(
                assignedEvaluationId,
                tabMenuItems[0].hash
            )
        ) {
            self.tabbedDetails.removeTabView(
                assignedEvaluationId,
                tabMenuItems[0].hash
            );
            if (!_.isUndefined(activateTabKey)) {
                // allow null
                self.tabbedDetails.setActiveTabView(activateTabKey);
            }
        }
    }

    self.deleteEvaluation = function (conversationId, assignedEvaluationId) {
        if (qualityAuthorization.canDeleteEvaluation()) {
            return qualityManagementService
                .deleteEvaluation(conversationId, assignedEvaluationId)
                .then(function (result) {
                    toasts.notifyMessage(
                        "playback.nav.evaluation.deleted.title",
                        {
                            bodyMessageKey:
                                "playback.nav.evaluation.deleted.msg",
                        }
                    );
                    return result;
                })
                .catch(function () {
                    toasts.notifyMinorAlert(
                        "playback.nav.evaluation.not.deleted.title",
                        {
                            bodyMessageKey:
                                "playback.nav.evaluation.not.deleted.msg",
                        }
                    );
                });
        }
    };

    self.closePanel = function () {
        self.sidepanel.close();
    };

    /**
     * Adds the audit tab to the conversation page, then loads the template, along with the appropriate view
     * It also adds the css along with the label to the Audit Tab
     * @returns void
     */

    const addAuditTabView = function (
        templatePath,
        argumentsList,
        persistClasses,
        setPersistDOM
    ) {
        const tabContentViews = {};
        tabContentViews.audit = {
            view: AuditView,
            template: templatePath,
            arguments: argumentsList,
            persist: true,
            classNames: persistClasses,
            persistDOM: setPersistDOM,
            hash: navHelper.buildInteractionHash(
                "admin",
                self.conversationId(),
                "audit"
            ),
            localHash: `#/admin/${self.conversationId()}/audit`,
            tabId: PLAYBACK_NAV_TAB_IDS.audit,
        };
        const tabMenuItems = getTabMenuItems(
            PlaybackContext.ADMIN,
            self.conversationId(),
            "audit",
            "audit",
            PLAYBACK_NAV_TAB_IDS.audit,
            TAB_PLACEMENTS.audit
        );
        self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
    };

    const addAuditTab = function () {
        if (
            qualityAuthorization.canViewAudit() &&
            !self.tabbedDetails.containsTab("audit")
        ) {
            const templatePath = "templates/auditView";
            const argumentsList = [
                self.conversation,
                self.computed(() => {
                    const activeRecordingView = self.activeRecordingView();
                    const recording =
                        activeRecordingView &&
                        activeRecordingView.recording &&
                        activeRecordingView.recording();
                    return recording;
                }),
                conversationId,
            ];
            const persistClasses = {
                "gux-checked": !self.isFlareThemeEnabled,
                "gux-unchecked": !self.isFlareThemeEnabled,
                "details-tab-content-no-padding": !self.isFlareThemeEnabled,
                "details-tab-content-no-border": !self.isFlareThemeEnabled,
                "panel-content-no-padding": self.isFlareThemeEnabled,
                "panel-content-no-border": self.isFlareThemeEnabled,
            };
            const setPersistDOM = true;
            addAuditTabView(
                templatePath,
                argumentsList,
                persistClasses,
                setPersistDOM
            );
        }
    };

    /**
     * Activates the audit tab. This is getting called by qm-routes.
     */
    self.activateAuditTab = function () {
        stopTabDefaultBehavior();
        addAuditTab();
        self.tabbedDetails.setActiveTabView("audit");
    };

    /**
     * Activates the conversation timeline tab. This is getting called by qm-routes.
     */
    self.activateTimelineTab = function () {
        stopTabDefaultBehavior();
        addTimelineTab();
        self.tabbedDetails.setActiveTabView("timeline");
    };

    const deleteCalibrationCallback = function (calibrationId, evaluationIds) {
        if (qualityAuthorization.canDeleteCalibration()) {
            evaluationIds.forEach((evaluationId) =>
                self.removeSidePanelEvaluation(evaluationId)
            );
            return qualityManagementService
                .deleteCalibration(calibrationId)
                .then(function () {
                    self.calibrations().entities = self
                        .calibrations()
                        .entities.filter(
                            (entity) => entity.id !== calibrationId
                        );
                    self.tabbedDetails.removeActiveTabView();
                    toasts.notifyMessage(
                        "playback.nav.calibration.deleted.title",
                        {
                            bodyMessageKey:
                                "playback.nav.calibration.deleted.msg",
                        }
                    );
                })
                .catch(function () {
                    toasts.notifyMinorAlert(
                        "playback.nav.calibration.not.deleted.title",
                        {
                            bodyMessageKey:
                                "playback.nav.calibration.not.deleted.msg",
                        }
                    );
                });
        }
    };

    self.addSidePanelEvaluation = async function (
        conversationId,
        assignedEvaluationId,
        isAdminContext
    ) {
        if (!self.sidepanel.containsView(assignedEvaluationId)) {
            const evaluation =
                await qualityManagementService.getAssignedEvaluation(
                    conversationId,
                    assignedEvaluationId
                );
            const args = [
                FormType.evaluation,
                assignedEvaluationId,
                conversationId,
                isAdminContext,
                self.deleteEvaluation,
                self.closePanel,
            ];
            const isAssigneeAndDisputesFTEnabled =
                featureToggleService.isEnabled(
                    FeatureToggle.EVALUATION_ASSIGNEE
                ) &&
                featureToggleService.isEnabled(
                    FeatureToggle.EVALUATION_DISPUTES
                );
            const queryParams = getDecodedQueryParamsFromWindow();
            if (isAssigneeAndDisputesFTEnabled) {
                args.push(queryParams.assignmentPanel);
            }
            const qualityFormTimeMachineBranch = queryParams.qualityFormVersion;
            if (qualityFormTimeMachineBranch) {
                args.push(qualityFormTimeMachineBranch);
            }
            self.sidepanel.addView(assignedEvaluationId, {
                view: EmbeddedQualityFormEditor,
                viewTitle: evaluation.evaluationForm.name,
                onClose: () => {
                    navHelper.navigateTo(
                        navHelper.asHostHash(
                            removeQueryParamFromWindow("evaluationId")
                        ),
                        undefined,
                        false
                    );
                },
                arguments: args,
                template: "templates/composer/forms/embeddedQualityFormEditor",
                persist: true,
                showHeader: false,
            });
        }
    };

    self.activateSidePanelEvaluation = async function (assignedEvaluationId) {
        const isAdminContext = hashContext === PlaybackContext.ADMIN;
        await self.addSidePanelEvaluation(
            self.conversationId(),
            assignedEvaluationId,
            isAdminContext
        );
        self.sidepanel.setActiveView(
            assignedEvaluationId,
            self.conversationId(),
            assignedEvaluationId,
            isAdminContext,
            self.deleteEvaluation
        );
    };

    self.removeSidePanelEvaluation = function (assignedEvaluationId) {
        self.sidepanel.removeView(assignedEvaluationId);
    };

    self.subscribe(self.evaluationId, (evaluationId) => {
        if (evaluationId) {
            self.activateSidePanelEvaluation(evaluationId);
        }
    });

    self.activateSurvey = async function (surveyId) {
        if (qualityAuthorization.canViewSurvey()) {
            if (!self.sidepanel.containsView(surveyId)) {
                const survey = await qualityManagementService.getSurvey(
                    surveyId
                );
                self.sidepanel.addView(surveyId, {
                    view: EmbeddedQualityFormEditor,
                    arguments: [
                        FormType.survey,
                        surveyId,
                        conversationId,
                        undefined,
                        undefined,
                        self.closePanel,
                    ],
                    template:
                        "templates/composer/forms/embeddedQualityFormEditor",
                    persist: true,
                    showHeader: false,
                    viewTitle: survey.surveyForm.name,
                    onClose: () => {
                        navHelper.navigateTo(
                            navHelper.asHostHash(
                                removeQueryParamFromWindow("surveyId")
                            ),
                            undefined,
                            false
                        );
                    },
                });
            }
            self.sidepanel.setActiveView(surveyId);
        }
    };

    self.subscribe(self.surveyId, (surveyId) => {
        if (surveyId) {
            self.activateSurvey(surveyId);
        }
    });

    self.activateCreateEvaluationView = function () {
        stopTabDefaultBehavior();
        if (qualityAuthorization.canAddEvaluation()) {
            self.sidepanel.addView("newEvaluation", {
                view: CreateEvaluationView,
                template: "templates/createEvaluation",
                viewTitle: ko.i18n.renderString(
                    "playback.evaluation.newEvalauation"
                ),
                onClose: () => {
                    if (getHashRoute().match(/evaluation\/new$/)) {
                        navigateToQualitySummary();
                    }
                },
            });
            self.sidepanel.setActiveView(
                "newEvaluation",
                self.conversation,
                newEvaluationCallback,
                navigateToQualitySummary, // on cancel
                createCalibrationCallback,
                false,
                self.conversationDetailsView.internalParticipantViews,
                conversationId
            );
        }
    };

    self.deactivateCreateEvaluationView = function () {
        self.sidepanel.removeView("newEvaluation");
    };

    self.activateCreateCalibrationView = function () {
        stopTabDefaultBehavior();
        if (qualityAuthorization.canAddEvaluation()) {
            self.sidepanel.addView("newCalibration", {
                view: CreateEvaluationView,
                template: "templates/createEvaluation",
                viewTitle: ko.i18n.renderString(
                    "playback.calibration.newCalibration"
                ),
                onClose: () => {
                    if (getHashRoute().match(/calibration\/new$/)) {
                        navigateToQualitySummary();
                    }
                },
            });
            self.sidepanel.setActiveView(
                "newCalibration",
                self.conversation,
                newEvaluationCallback,
                navigateToQualitySummary, // on cancel
                createCalibrationCallback,
                true,
                self.conversationDetailsView.internalParticipantViews,
                conversationId
            );
        }
    };

    self.deactivateCreateCalibrationView = function () {
        self.sidepanel.removeView("newCalibration");
    };

    const addCalibrationTab = function (calibrationId) {
        if (qualityAuthorization.canViewCalibration()) {
            if (!self.tabbedDetails.containsTab(calibrationId)) {
                const tabContentViews = {};
                const calibrationTabId = `${PLAYBACK_NAV_TAB_IDS.calibration}-${calibrationId}`;
                tabContentViews[calibrationId] = {
                    view: CalibrationResultsView,
                    template: "templates/calibrationResults",
                    arguments: [calibrationId, deleteCalibrationCallback],
                    persist: true,
                    tabId: calibrationTabId,
                };
                const tabMenuItems = getTabMenuItems(
                    PlaybackContext.ADMIN,
                    self.conversationId(),
                    "calibrations/" + calibrationId,
                    "calibration",
                    calibrationTabId,
                    TAB_PLACEMENTS.calibration
                );
                self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
            }
        }
    };

    const prepCalibrationTab = function (calibrationId) {
        if (
            self
                .calibrations()
                .entities.find(
                    (calibration) => calibration.id === calibrationId
                ) !== undefined
        ) {
            stopTabDefaultBehavior();
            addCalibrationTab(calibrationId);
            self.tabbedDetails.setActiveTabView(
                calibrationId,
                calibrationId,
                deleteCalibrationCallback
            );
        } else {
            toasts.notifyMajorAlert(undefined, {
                bodyMessageKey:
                    "calibrationResults.getCalibrationRequest.error.default",
            });
            LOG.info("Calibration not found:", calibrationId);
            navHelper.navigateTo(
                navHelper.asHostHash(
                    `#/admin/${self.conversationId()}/details`
                ),
                undefined,
                false
            );
        }
    };

    self.activateCalibrationTab = function (calibrationId) {
        if (
            self.calibrations &&
            self.calibrations() &&
            self.calibrations().entities
        ) {
            prepCalibrationTab(calibrationId);
        } else {
            self.subscribe(self.calibrations, function () {
                prepCalibrationTab(calibrationId);
            });
        }
    };

    const addQualitySummaryTab = function () {
        if (
            qualityAuthorization.canAddEvaluation() ||
            qualityAuthorization.canDeleteEvaluation() ||
            qualityAuthorization.canAddCalibration() ||
            qualityAuthorization.canViewSurvey()
        ) {
            if (!self.tabbedDetails.containsTab("qualitySummaryView")) {
                const tabContentViews = {
                    qualitySummaryView: {
                        view: QualitySummaryView,
                        template: "templates/qualitySummary",
                        arguments: [
                            self.conversationId,
                            self.hasParticipantToEvaluate,
                            self.deleteEvaluation,
                        ],
                        persist: true,
                        tabId: PLAYBACK_NAV_TAB_IDS.qualitySummary,
                    },
                };
                const tabMenuItems = getTabMenuItems(
                    PlaybackContext.ADMIN,
                    self.conversationId(),
                    "qualitySummary",
                    "qualitySummary",
                    PLAYBACK_NAV_TAB_IDS.qualitySummary,
                    TAB_PLACEMENTS.qualitySummary
                );
                self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
            }
        }
    };

    self.activateQualitySummaryTab = function () {
        stopTabDefaultBehavior();
        addQualitySummaryTab();
        activateTabIfNeeded("qualitySummaryView");
    };

    /* Customer Journey Tab */

    self.activeCustomerJourneySessionId = ko.observable(null);

    const addCustomerJourneyTab = function (journeyContexts) {
        const hasJourneys = journeyContexts && !_.isEmpty(journeyContexts);
        const journeyTabExists = self.tabbedDetails.containsTab("journeys");
        const context = hashContext || PlaybackContext.AGENT;

        if (
            !hasJourneys ||
            journeyTabExists ||
            !self.canViewConversationCustomerJourney
        ) {
            return;
        }

        const tabContentViews = {};
        tabContentViews.journeys = {
            view: CustomerJourneyView,
            arguments: [journeyContexts, self.activeCustomerJourneySessionId],
            template: "templates/customerJourney",
            persist: true,
            tabId: PLAYBACK_NAV_TAB_IDS.journeys,
        };
        const tabMenuItems = getTabMenuItems(
            context,
            self.conversationId(),
            "journeys",
            "journeys",
            PLAYBACK_NAV_TAB_IDS.journeys,
            TAB_PLACEMENTS.journeys
        );
        self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
    };

    self.activateCustomerJourneyTab = function (sessionId) {
        stopTabDefaultBehavior();
        self.activeCustomerJourneySessionId(sessionId);
        return new Bluebird(function (resolve) {
            const conversationDetailsV6 = self.conversationDetailsV6();
            if (conversationDetailsV6) {
                // if we already have a conversation, use that
                resolve(conversationDetailsV6);
            } else {
                // if called from the route handler, the conversation may not yet be available.
                // setting up a self-disposing subscription handles this case
                const convSubs = self.subscribe(
                    self.conversationDetailsV6,
                    function (nextConversation) {
                        if (nextConversation) {
                            convSubs.dispose();
                            resolve(nextConversation);
                        }
                    }
                );
            }
        }).then(function (conversationDetailsV6) {
            const conversationJourneyContexts = pickContextData(
                conversationDetailsV6
            );
            addCustomerJourneyTab(conversationJourneyContexts);
            activateTabIfNeeded("journeys");
        });
    };

    /* End Customer Journey Tab */

    /* GIA Transcript Tab */

    const transcriptTabId = "transcript";

    /**
     * memoized function that conditionally adds the GIA transcript tab.
     * conditions:
     *  * user has recording:recording:view permission
     *  * the feature toggle is enabled
     *  OR
     *  * user has recording:recordingSegment:view permission
     *  * the feature toggle is enabled
     *
     * @returns {Bluebird<boolean>}
     */
    const addTranscriptTab = _.memoize(function () {
        if (
            !qualityAuthorization.hasViewRecordingPermission() &&
            !qualityAuthorization.hasViewRecordingSegmentPermission()
        ) {
            LOG.info(
                "recording:view AND recordingSegment:view permission missing.  Not adding transcript tab.",
                {
                    hasViewRecordingPermission:
                        qualityAuthorization.hasViewRecordingPermission(),
                    hasViewRecordingSegmentPermission:
                        qualityAuthorization.hasViewRecordingSegmentPermission(),
                }
            );
        } else {
            const tabContentViews = {
                [transcriptTabId]: {
                    view: GIATranscriptView,
                    template: "templates/giaTranscript",
                    arguments: [
                        self.computed(() => {
                            let conversationDetails =
                                self.conversationDetailsV6();
                            if (
                                conversationDetails === null &&
                                featureToggleService.isEnabled(
                                    FeatureToggle.TARTARUS_AS_CONVERSATION_SOURCE
                                )
                            ) {
                                conversationDetails = {
                                    conversationId: self.conversationId(),
                                };
                            }
                            return conversationDetails;
                        }),
                        self.isTranscriptLoading,
                        self.recordingManagerView.httpErrorStatus,
                        self.computed(() =>
                            self.activeRecordingView()?.recording?.()
                        ),
                        self.recordingManagerView.conversationRecordings,
                        self.isActiveInteraction,
                        self.computed(() => {
                            const activeRecordingView =
                                self.activeRecordingView();
                            if (
                                activeRecordingView &&
                                activeRecordingView.isRecordingAvailable
                            ) {
                                return activeRecordingView.isRecordingAvailable();
                            }
                            return undefined;
                        }),
                    ],
                    classNames: {
                        "extend-details-tab-view": !self.isFlareThemeEnabled,
                        "extend-panel-content-view": self.isFlareThemeEnabled,
                    },
                    persistDOM: true,
                    tabId: PLAYBACK_NAV_TAB_IDS.transcript,
                },
            };
            const tabMenuItems = getTabMenuItems(
                hashContext || PlaybackContext.ADMIN,
                self.conversationId(),
                "transcript",
                "transcript",
                PLAYBACK_NAV_TAB_IDS.transcript,
                TAB_PLACEMENTS.transcript
            );
            self.tabbedDetails.addTabViews(tabMenuItems, tabContentViews);
        }
        return;
    });

    /* End GIA Transcript Tab */

    /**
     * Choose default to transcript or details tab according to response from transcript-ui.
     */
    self.chooseDefaultTab = function (event) {
        if (!event) {
            return;
        }

        const payload = event.data;
        if (payload.key == "hasAvailableTranscript") {
            // Remove event listener to avoid repeat messages
            window.removeEventListener("message", self.chooseDefaultTab);

            // Set default behavior if redirect is needed without reloading the app
            // Save the conversation ID to avoid redirecting when switching interactions
            hasAvailableTranscript = {
                conversationId: self.conversationId(),
                enableDefault: payload.transfer,
            };
            if (self.tabRedirect()) {
                self.navigateDefaultTab();
            }
        }
    };

    /**
     * Redirects to the details tab if the recording does not have a transcript.
     * Otherwise, formally navigate to the transcript tab
     */
    self.navigateDefaultTab = function () {
        if (
            hasAvailableTranscript &&
            self.conversationId() === hasAvailableTranscript.conversationId &&
            hasAvailableTranscript.enableDefault
        ) {
            // set tab view to transcript
            if (
                self.tabbedDetails.containsTab(transcriptTabId) &&
                self.tabbedDetails.currentViewData().viewKey !== transcriptTabId
            ) {
                self.tabbedDetails.setActiveTabView(transcriptTabId);
            }

            // force the active nav item to transcript
            activateLastNavItemForEndpoint(
                self.conversationId(),
                "transcript",
                "transcript",
                PLAYBACK_NAV_TAB_IDS.transcript,
                TAB_PLACEMENTS.transcript,
                self.tabbedDetails.navMenu
            );
            return;
        }
        self.activateConversationDetailsTab();
    };

    /**
     * Defaults the base conversation route to the GIA transcript tab.
     * Sets the event listener that will choose a path based a transcript-ui response.
     * Navigates accordingly if event listener has already responded.
     */
    self.activateDefaultTab = function () {
        if (
            qualityAuthorization.hasViewRecordingPermission() ||
            qualityAuthorization.hasViewRecordingSegmentPermission()
        ) {
            if (typeof hasAvailableTranscript !== "undefined") {
                // If hasAvailableTranscript has already been set, then navigate accordingly
                self.navigateDefaultTab();
                return Bluebird.resolve();
            }

            // Listen while Transcript-UI checks for available transcript
            self.tabRedirect(true);
            window.removeEventListener("message", self.chooseDefaultTab);
            window.addEventListener("message", self.chooseDefaultTab);

            // Activate transcript tab while waiting for a response from Transcript-UI
            return self.activateTranscriptTab();
        }

        hasAvailableTranscript = false;
        self.navigateDefaultTab();
        return Bluebird.resolve();
    };

    /**
     * activate the GIA transcript tab. redirects to the details tab
     * if the tab is both not present and fails to be added to the view.
     */
    self.activateTranscriptTab = function () {
        const context =
            hashContext === PlaybackContext.ADMIN
                ? PlaybackContext.ADMIN
                : PlaybackContext.AGENT;
        const navigateDetailsUrl = navHelper.buildInteractionHash(
            context,
            self.conversationId(),
            "details"
        );
        if (
            !qualityAuthorization.hasViewRecordingPermission() &&
            !qualityAuthorization.hasViewRecordingSegmentPermission()
        ) {
            toasts.notifyMessage(undefined, {
                bodyMessageKey: "config.error.forbidden",
            });
            return navHelper.navigateTo(navigateDetailsUrl);
        }
        if (self.tabbedDetails.containsTab(transcriptTabId)) {
            return Bluebird.resolve(
                self.tabbedDetails.setActiveTabView(transcriptTabId)
            );
        }
        return addTranscriptTab()
            .then(() => {
                self.tabbedDetails.setActiveTabView(transcriptTabId);
            })
            .catch(() => {
                LOG.info("Failed to add transcript tab.  Redirecting...", {
                    from: window.location.hash,
                    to: navigateDetailsUrl,
                });
                return navHelper.navigateTo(navigateDetailsUrl);
            });
    };

    self.handleEvaluationNotification = function (evaluationNotification) {
        if (
            evaluationNotification.eventType ===
            EvaluationSubmittedEventType.AI_SCORED
        ) {
            const isAiScoringToggleEnabled = featureToggleService.isEnabled(
                FeatureToggle.WEM_964_AI_SCORING
            );
            if (!isAiScoringToggleEnabled) {
                return;
            }
            const evaluationId = evaluationNotification.id;
            if (self.sidepanel.containsView(evaluationId)) {
                if (self.sidepanel.isActiveView(evaluationId)) {
                    // When we receive an AI_SCORED event for the evaluation that has an active side panel,
                    // we will send a message to the child quality forms iFrame
                    // so that it can fetch latest AI Scoring answer data
                    const qfWindow = self.sidepanel.currentViewData().view;
                    const notificationData = {
                        evaluationNotificationEventType:
                            evaluationNotification.eventType,
                    };
                    qfWindow.postMessageToIFrame(notificationData);
                } else {
                    // When we get the AI_SCORED type event for the hidden evaluation
                    // we will refresh the side panel component.
                    const isAdminContext =
                        hashContext === PlaybackContext.ADMIN;
                    self.removeSidePanelEvaluation(evaluationId);
                    self.addSidePanelEvaluation(
                        self.conversationId(),
                        evaluationId,
                        isAdminContext
                    );
                }
            }
        }
        if (
            evaluationNotification.eventType ===
            EvaluationSubmittedEventType.DELETED
        ) {
            self.sidepanel.removeViews(true);
            if (
                evaluationNotification.id &&
                evaluationNotification.id !== self.evaluationId()
            ) {
                self.activateSidePanelEvaluation(self.evaluationId());
            }
        }
        if (
            evaluationNotification.eventType ===
            EvaluationSubmittedEventType.SUBMISSION_RESCINDED
        ) {
            self.sidepanel.removeViews(false);
        }
        if (
            evaluationNotification.eventType ===
                EvaluationSubmittedEventType.ASSIGNED &&
            evaluationNotification.calibration
        ) {
            /**
             * Push notification of createCalibration to avoid creating false callBack
             * This notification only holds calibration.id
             **/
            const calibrationId = evaluationNotification.calibration.id;
            const calibration = self
                .calibrations()
                .entities.find((entity) => entity.id === calibrationId);
            if (!calibration?.id) {
                self.calibrations().entities.push(
                    evaluationNotification.calibration
                );
            }
        }
        if (
            isAutomatedEvaluationCompletionToggleEnabled &&
            evaluationNotification.eventType ===
                EvaluationSubmittedEventType.SUBMITTED
        ) {
            qualityManagementService
                .getAssignedEvaluation(
                    self.conversationId(),
                    evaluationNotification.id
                )
                .then(function (response) {
                    if (response && response?.systemSubmitted === true) {
                        toasts.notifyMessage(
                            "evaluationSummary.evaluationSubmitted",
                            {
                                icon: "pc-icon-messages",
                            }
                        );
                    }
                })
                .catch(function (error) {
                    LOG.error(
                        "Failed to get assigned evaluation for SUBMITTED event",
                        {
                            conversationId: self.conversationId(),
                            evaluationId: evaluationNotification.id,
                            error: error,
                        }
                    );
                });
        }
    };

    self.hasRestoreHeader = self.computed(() => {
        return (
            self.recordingManagerView.hasRecordings() &&
            self.recordingManagerView.selectedRecording() &&
            self.recordingManagerView.selectedRecording()
                .recordingRestoreDetails &&
            self.recordingManagerView
                .selectedRecording()
                .recordingRestoreDetails.hasRestoreHeader()
        );
    });

    self.isEmailView = self.computed(() => {
        return (
            self.recordingManagerView.hasRecordings() &&
            self.recordingManagerView.selectedRecording() &&
            self.recordingManagerView.selectedRecording()
                .recordingRestoreDetails &&
            (self.recordingManagerView
                .selectedRecording()
                .recordingRestoreDetails.isEmail() ||
                self.recordingManagerView
                    .selectedRecording()
                    .recordingRestoreDetails.isEmailContainer())
        );
    });

    /**
     * SUBSCRIPTIONS
     */

    self.fetchRecordingsError = self.computed(() => {
        const statusCode = self.recordingManagerView.httpErrorStatus();
        if (statusCode) {
            self.isTranscriptLoading(false);
        }
        return statusCode && statusCode >= 400;
    });

    self.subscribe(self.isActiveInteraction, () => {
        self.recordingManagerView.callRecordingItemContainer(
            self.isActiveInteraction(),
            self.conversationId()
        );
    });

    self.subscribe(self.conversation, function (nextConversation) {
        if (nextConversation && nextConversation.participants) {
            self.hasParticipantToEvaluate(
                checkInternalParticipant(nextConversation.participants)
            );
            self.needsWrapup(checkIfNeedsWrapup(nextConversation.participants));
            self.acdVoicemailIds(getAcdVoicemailIds(nextConversation));

            self.isCurrentUserOnConversation(
                _.some(nextConversation.participants(), function (participant) {
                    return (
                        userService.currentUser() &&
                        userService.currentUser().id &&
                        userService.currentUser().id === participant.userId
                    );
                })
            );
        }
    });

    self.subscribe(self.conversationId, function () {
        getConversation();
        getAnalyticsV6ConversationDetails(self.conversationId());

        if (qualityAuthorization.canViewCalibration()) {
            getCalibrations();
        }

        addConversationTab(self.conversationId());
        addTimelineTab(self.conversationId());
        addQualitySummaryTab();
        addAuditTab();
    });

    /**
     * A helper function to get the tab content for a given nav item.
     * Used to determine if the tab content should be rendered.
     * @param {string} tabId - the id of the tab to get the content for
     * @param {Object | Observable<Object>} currentViewData - the current view data
     * @param {Array | ObservableArray<Object>} persistentDOMTabViews - the persistent DOM tab views
     * @returns {Object} - object with shouldRender, classNames, template, and view properties
     */
    self.getTabContentForNavItem = function (
        tabId,
        currentViewData,
        persistentDOMTabViews
    ) {
        const persistentViews = ko.unwrap(persistentDOMTabViews);
        const persistDOMView = _.find(persistentViews, { tabId: tabId });
        if (persistDOMView) {
            const isTranscript =
                persistDOMView.tabId === PLAYBACK_NAV_TAB_IDS.transcript;
            const shouldWait = self.shouldWaitForRecordingLoad();
            const isLoading = self.isLoadingMultiple();

            return {
                shouldRender:
                    (isTranscript && !shouldWait) ||
                    (!isTranscript && !isLoading),
                classNames: persistDOMView.classNames,
                template: persistDOMView.template,
                view: persistDOMView.view,
            };
        }

        const currentView = ko.unwrap(currentViewData);
        if (
            currentView &&
            !currentView.isPersistDOM &&
            currentView.tabId === tabId
        ) {
            return {
                shouldRender: !self.isLoadingMultiple(),
                classNames: currentView.classNames,
                template: currentView.template,
                view: currentView.view,
            };
        }
        return { shouldRender: false };
    };

    //add the conversation details
    addConversationTab(self.conversationId());
    addTimelineTab(self.conversationId());

    addQualitySummaryTab();

    addAuditTab();

    addTimelineTab();

    if (
        qualityAuthorization.hasViewRecordingPermission() ||
        qualityAuthorization.hasViewRecordingSegmentPermission()
    ) {
        addTranscriptTab();
    }

    self.subscribe(self.calibrations, function (newVal) {
        _.each(newVal.entities, function (calibration) {
            addCalibrationTab(calibration.id);
        });
    });

    self.subscribe(self.journeyContexts, function (journeyContexts) {
        const hasJourneysTab = self.tabbedDetails.containsTab("journeys");
        if (!hasJourneysTab && journeyContexts && journeyContexts.length) {
            addCustomerJourneyTab(journeyContexts);
        }
    });

    notificationService.subscribe(
        evaluationNotificationTopic,
        self.handleEvaluationNotification
    );

    /**
     * CLEAN UP
     */
    self.onDispose = function () {
        // Set disposal flag
        isViewDisposed = true;

        clearInterval(activeConversationUpdateInterval);
        if (cleanupVisibilityHandler) {
            cleanupVisibilityHandler();
        }
    };
});

export default PlaybackView;
